import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs';
import { GungEdiInstanceConfiguration, GungEdiMessageSendStatus } from '../../../models/edi';
import { EdiSavedMappingModelSummary } from '../../../models/edi-mapping-modeller';
import { AuthService } from '../../../services/auth/auth.service';
import { BackendFeatureService } from '../../../services/backend-feature.service';
import { EdiService } from '../../../services/edi/edi.service';
import { GungModalService } from '../../../services/gung-modal/gung-modal.service';

type EdiConfigurationFieldType = 'text' | 'number' | 'textarea' | 'array' | 'status' | 'boolean';

interface EdiConfigurationField {
  key: string;
  label: string;
  type: EdiConfigurationFieldType;
  required?: boolean;
  rows?: number;
}

interface EdiConfigurationFieldGroup {
  title: string;
  fields: EdiConfigurationField[];
}

@Component({
  selector: 'lib-edi-configurations',
  templateUrl: './edi-configurations.component.html',
  styleUrls: ['./edi-configurations.component.css'],
  standalone: false
})
export class EdiConfigurationsComponent implements OnInit {
  readonly mappingTransformerQualifier = 'MAPPING_INSTRUCTION_V1';
  readonly mappingRuntimeFeatureId = 'gung-feature-edi.mapping-runtime';
  protected ediService = inject(EdiService);
  protected authService = inject(AuthService);
  protected backendFeatureService = inject(BackendFeatureService);
  protected formBuilder = inject(FormBuilder);
  protected gungModalService = inject(GungModalService);
  protected translateService = inject(TranslateService);

  configurations: GungEdiInstanceConfiguration[] = [];
  selectedConfiguration: GungEdiInstanceConfiguration | null = null;
  form: FormGroup;
  loading = false;
  saving = false;
  isActuator = false;
  loadError = '';
  saveError = '';
  filterText = '';
  mappingRuntimeAvailable = false;
  savedOrderMappingModels: EdiSavedMappingModelSummary[] = [];
  mappingModelVersions: EdiSavedMappingModelSummary[] = [];
  mappingModelsLoading = false;
  mappingModelsError = '';

  sendStatuses = Object.values(GungEdiMessageSendStatus);

  fieldGroups: EdiConfigurationFieldGroup[] = [
    {
      title: 'General',
      fields: [
        { key: 'instanceName', label: 'Instance name', type: 'text' },
        { key: 'enabled', label: 'Enabled', type: 'boolean' },
        { key: 'edifactVersion', label: 'EDIFACT version', type: 'text' },
        { key: 'targetCustomer', label: 'Target customer', type: 'text' },
        { key: 'targetOrderChannel', label: 'Target order channel', type: 'text' },
        { key: 'externalSupplierId', label: 'External supplier ID', type: 'text' },
        { key: 'fileEncoding', label: 'File encoding', type: 'text' },
        { key: 'defaultBuyerGln', label: 'Default buyer GLN', type: 'number' },
        { key: 'defaultRecipientGln', label: 'Default recipient GLN', type: 'number' },
        { key: 'maxSendAttemptsBeforeError', label: 'Max send attempts before error', type: 'number', required: true },
        { key: 'qualifyingFileContents', label: 'Qualifying file contents', type: 'textarea', rows: 3 }
      ]
    },
    {
      title: 'FTP/SFTP',
      fields: [
        { key: 'ftpProtocol', label: 'Protocol', type: 'text' },
        { key: 'ftpServer', label: 'Server', type: 'text' },
        { key: 'ftpPort', label: 'Port', type: 'number' },
        { key: 'ftpUsername', label: 'Username', type: 'text' }
      ]
    },
    {
      title: 'Email',
      fields: [
        { key: 'sendByEmail', label: 'Send by email', type: 'boolean' },
        { key: 'sendIncomingByEmail', label: 'Send incoming by email', type: 'boolean' },
        { key: 'sendByEmailRecipients', label: 'Default recipients', type: 'array' },
        { key: 'sendByEmailORDERSRecipients', label: 'ORDERS recipients', type: 'array' },
        { key: 'sendByEmailORDRSPRecipients', label: 'ORDRSP recipients', type: 'array' },
        { key: 'sendByEmailDESADVRecipients', label: 'DESADV recipients', type: 'array' },
        { key: 'sendByEmailINVOICRecipients', label: 'INVOIC recipients', type: 'array' }
      ]
    },
    {
      title: 'Message statuses',
      fields: [
        { key: 'initialStatusForValidOrders', label: 'Valid orders', type: 'status', required: true },
        { key: 'initialStatusForValidOrderResponses', label: 'Valid order responses', type: 'status', required: true },
        {
          key: 'initialStatusForValidDespatchAdvices',
          label: 'Valid despatch advices',
          type: 'status',
          required: true
        },
        { key: 'initialStatusForValidInvoices', label: 'Valid invoices', type: 'status', required: true }
      ]
    },
    {
      title: 'EDIFACT transformers',
      fields: [
        {
          key: 'edifactOrdersTransformerQualifier',
          label: 'ORDERS qualifier (MAPPING_INSTRUCTION_V1 uses a saved model)',
          type: 'text'
        },
        { key: 'edifactOrdrspTransformerQualifier', label: 'ORDRSP qualifier', type: 'text' },
        { key: 'edifactDesadvTransformerQualifier', label: 'DESADV qualifier', type: 'text' },
        { key: 'edifactInvoicTransformerQualifier', label: 'INVOIC qualifier', type: 'text' }
      ]
    },
    {
      title: 'Folders',
      fields: [
        { key: 'ftpInboundFolder', label: 'FTP inbound folder', type: 'text' },
        { key: 'ftpOutboundFolder', label: 'FTP outbound folder', type: 'text' },
        { key: 'ftpOrderInboundFolder', label: 'FTP order inbound folder', type: 'text' },
        { key: 'ftpOrdRspOutboundFolder', label: 'FTP order response outbound folder', type: 'text' },
        { key: 'ftpInvoiceOutboundFolder', label: 'FTP invoice outbound folder', type: 'text' },
        { key: 'ftpDesAdvOutboundFolder', label: 'FTP despatch advice outbound folder', type: 'text' },
        { key: 'ftpInvrptOutboundFolder', label: 'FTP inventory report outbound folder', type: 'text' },
        { key: 'ftpPricatOutboundFolder', label: 'FTP PRICAT outbound folder', type: 'text' },
        { key: 'localOrderFolder', label: 'Local order folder', type: 'text' },
        { key: 'localOrderResponseFolder', label: 'Local order response folder', type: 'text' },
        { key: 'localDespatchAdviseFolder', label: 'Local despatch advice folder', type: 'text' },
        { key: 'localInvoiceFolder', label: 'Local invoice folder', type: 'text' },
        { key: 'localInventoryReportFolder', label: 'Local inventory report folder', type: 'text' },
        { key: 'localTestOrderFile', label: 'Local test order file', type: 'text' }
      ]
    },
    {
      title: 'Flags/options',
      fields: [
        { key: 'useB24MessageFlags', label: 'Use B24 message flags', type: 'boolean' },
        { key: 'usePricesFromOrder', label: 'Use prices from order', type: 'boolean' },
        { key: 'addDeletedItemsToOrdRsp', label: 'Add deleted items to ORDRSP', type: 'boolean' },
        { key: 'groupOnShipments', label: 'Group on shipments', type: 'boolean' },
        { key: 'groupOnConsigneeOrderId', label: 'Group on consignee order ID', type: 'boolean' },
        { key: 'groupOnShipmentIdAndConsignee', label: 'Group on shipment ID and consignee', type: 'boolean' },
        { key: 'localTest', label: 'Local test', type: 'boolean' },
        { key: 'copyConsigneeFromIncomingOrderToInvoice', label: 'Copy consignee to invoice', type: 'boolean' },
        { key: 'copyConsigneeFromIncomingOrderToDesadv', label: 'Copy consignee to DESADV', type: 'boolean' },
        { key: 'allowNonPrimaryGln', label: 'Allow non-primary GLN', type: 'boolean' },
        { key: 'allowCustomerIdInsteadOfGln', label: 'Allow customer ID instead of GLN', type: 'boolean' }
      ]
    }
  ];

  sensitiveFields: EdiConfigurationField[] = [
    { key: 'ftpPassword', label: 'FTP password', type: 'text' },
    { key: 'ftpPrivateKeyPath', label: 'FTP private key path', type: 'text' },
    { key: 'ftpPublicKeyPath', label: 'FTP public key path', type: 'text' },
    { key: 'ftpPublicKeyPasswordEncoded', label: 'FTP public key password encoded', type: 'text' }
  ];

  protected readonly stringFields = this.flattenFields(['text', 'textarea']);
  protected readonly numberFields = this.flattenFields(['number']);
  protected readonly booleanFields = this.flattenFields(['boolean']);
  protected readonly arrayFields = this.flattenFields(['array']);
  protected readonly statusFields = this.flattenFields(['status']);

  ngOnInit(): void {
    this.form = this.buildForm();
    this.authService
      .getHasRoles('ACTUATOR')
      .pipe(first())
      .subscribe(isActuator => {
        this.isActuator = isActuator;
        if (isActuator) {
          this.backendFeatureService
            .isActivated(this.mappingRuntimeFeatureId)
            .pipe(first())
            .subscribe(available => {
              this.mappingRuntimeAvailable = available;
              if (available) {
                this.loadSavedMappingModels();
              }
            });
        }
      });
    this.loadConfigurations();
  }

  loadConfigurations(): void {
    const selectedId = this.selectedConfiguration?.id;
    this.loading = true;
    this.loadError = '';
    this.ediService.getConfigurations(true).subscribe(
      configurations => {
        this.configurations = this.sortConfigurations(configurations || []);
        const nextSelection =
          this.configurations.find(configuration => configuration.id === selectedId) || this.configurations[0] || null;
        this.selectConfiguration(nextSelection);
        this.loading = false;
      },
      error => {
        this.loadError = this.getErrorMessage(error);
        this.loading = false;
      }
    );
  }

  selectConfiguration(configuration: GungEdiInstanceConfiguration | null): void {
    this.selectedConfiguration = configuration ? this.cloneConfiguration(configuration) : null;
    this.saveError = '';
    if (this.selectedConfiguration) {
      this.patchForm(this.selectedConfiguration);
      if (this.mappingRuntimeAvailable && this.selectedConfiguration.incomingOrderMappingModelId) {
        this.loadMappingModelVersions(
          this.selectedConfiguration.incomingOrderMappingModelId,
          this.selectedConfiguration.incomingOrderMappingModelVersion
        );
      } else {
        this.mappingModelVersions = [];
      }
    } else if (this.form) {
      this.form.reset();
      this.form.markAsPristine();
    }
  }

  save(): void {
    if (!this.selectedConfiguration || !this.isActuator || this.form.invalid || this.saving) {
      return;
    }

    const pathId = this.selectedConfiguration.id;
    const payload = this.buildPayload(pathId);
    this.saving = true;
    this.saveError = '';

    this.ediService.putConfiguration(pathId, payload).subscribe(
      response => {
        const savedConfiguration = this.cloneConfiguration(response || payload);
        savedConfiguration.id = pathId;
        this.configurations = this.sortConfigurations(
          this.configurations.map(configuration => (configuration.id === pathId ? savedConfiguration : configuration))
        );
        this.selectConfiguration(savedConfiguration);
        this.saving = false;
        const msg = 'EDI configuration saved';
        this.gungModalService.openConfirmYesNoModal(undefined, msg, { size: 'sm' }, 'OK', null).then(_ => {});
      },
      error => {
        this.saveError = this.getErrorMessage(error);
        this.saving = false;
      }
    );
  }

  onFilterTextChange(value: string): void {
    this.filterText = value || '';
  }

  isSelected(configuration: GungEdiInstanceConfiguration): boolean {
    return !!this.selectedConfiguration && this.selectedConfiguration.id === configuration.id;
  }

  hasExistingSensitiveValue(key: string): boolean {
    const value = this.selectedConfiguration?.[key];
    return value !== undefined && value !== null && value !== '';
  }

  getSensitiveControlName(key: string, suffix: 'NewValue' | 'Clear'): string {
    return `${key}${suffix}`;
  }

  trackByConfigurationId(_index: number, configuration: GungEdiInstanceConfiguration): string {
    return configuration.id;
  }

  trackByFieldKey(_index: number, field: EdiConfigurationField): string {
    return field.key;
  }

  trackByMappingModel(_index: number, model: EdiSavedMappingModelSummary): string {
    return `${model.modelId}:${model.version}`;
  }

  get usesMappingInstructionTransformer(): boolean {
    return this.form?.get('edifactOrdersTransformerQualifier')?.value === this.mappingTransformerQualifier;
  }

  get mappingBindingInvalid(): boolean {
    return (
      this.usesMappingInstructionTransformer &&
      (!this.form?.get('incomingOrderMappingModelId')?.value ||
        !this.form?.get('incomingOrderMappingModelVersion')?.value)
    );
  }

  onMappingModelChange(modelId: string): void {
    this.form.get('incomingOrderMappingModelVersion')?.setValue(null);
    this.loadMappingModelVersions(modelId);
  }

  get filteredConfigurations(): GungEdiInstanceConfiguration[] {
    const filter = this.filterText.trim().toLowerCase();
    if (!filter) {
      return this.configurations;
    }

    return this.configurations.filter(configuration =>
      [
        configuration.id,
        configuration.instanceName,
        configuration.enabled ? 'enabled' : 'disabled',
        configuration.ftpProtocol,
        configuration.ftpServer
      ]
        .filter(value => value !== undefined && value !== null)
        .some(value => String(value).toLowerCase().includes(filter))
    );
  }

  protected buildForm(): FormGroup {
    const controls: { [s: string]: any } = {};
    this.stringFields.forEach(field => (controls[field.key] = this.formBuilder.control('')));
    this.numberFields.forEach(field => {
      controls[field.key] = field.required
        ? this.formBuilder.control(null, [Validators.required])
        : this.formBuilder.control(null);
    });
    this.booleanFields.forEach(field => (controls[field.key] = this.formBuilder.control(false)));
    this.arrayFields.forEach(field => (controls[field.key] = this.formBuilder.control('')));
    this.statusFields.forEach(field => {
      controls[field.key] = field.required
        ? this.formBuilder.control(null, [Validators.required])
        : this.formBuilder.control(null);
    });
    this.sensitiveFields.forEach(field => {
      controls[this.getSensitiveControlName(field.key, 'NewValue')] = this.formBuilder.control('');
      controls[this.getSensitiveControlName(field.key, 'Clear')] = this.formBuilder.control(false);
    });
    controls.incomingOrderMappingModelId = this.formBuilder.control('');
    controls.incomingOrderMappingModelVersion = this.formBuilder.control(null);
    return this.formBuilder.group(controls);
  }

  protected patchForm(configuration: GungEdiInstanceConfiguration): void {
    const values: { [s: string]: any } = {};
    this.stringFields.forEach(field => (values[field.key] = configuration[field.key] ?? ''));
    this.numberFields.forEach(field => (values[field.key] = configuration[field.key] ?? null));
    this.booleanFields.forEach(field => (values[field.key] = !!configuration[field.key]));
    this.arrayFields.forEach(field => (values[field.key] = this.joinArrayValue(configuration[field.key])));
    this.statusFields.forEach(field => {
      values[field.key] = configuration[field.key] ?? GungEdiMessageSendStatus.NotSent;
    });
    this.sensitiveFields.forEach(field => {
      values[this.getSensitiveControlName(field.key, 'NewValue')] = '';
      values[this.getSensitiveControlName(field.key, 'Clear')] = false;
    });
    values.incomingOrderMappingModelId = configuration.incomingOrderMappingModelId ?? '';
    values.incomingOrderMappingModelVersion = configuration.incomingOrderMappingModelVersion ?? null;
    this.form.reset(values);
    this.form.markAsPristine();
  }

  protected buildPayload(pathId: string): GungEdiInstanceConfiguration {
    const payload = this.cloneConfiguration(this.selectedConfiguration);
    payload.id = pathId;

    this.stringFields.forEach(field => this.applyOptionalString(payload, field.key));
    this.numberFields.forEach(field => this.applyNumber(payload, field.key, !!field.required));
    this.booleanFields.forEach(field => (payload[field.key] = !!this.form.get(field.key)?.value));
    this.arrayFields.forEach(field => (payload[field.key] = this.splitArrayValue(this.form.get(field.key)?.value)));
    this.statusFields.forEach(field => (payload[field.key] = this.form.get(field.key)?.value));
    this.applySensitiveFields(payload);
    this.applyOptionalString(payload, 'incomingOrderMappingModelId');
    this.applyNumber(payload, 'incomingOrderMappingModelVersion', false);

    return payload;
  }

  protected applyOptionalString(payload: GungEdiInstanceConfiguration, key: string): void {
    const value = this.form.get(key)?.value;
    if (value === undefined || value === null || value === '') {
      delete payload[key];
      return;
    }
    payload[key] = value;
  }

  protected applyNumber(payload: GungEdiInstanceConfiguration, key: string, required: boolean): void {
    const value = this.form.get(key)?.value;
    if (value === undefined || value === null || value === '') {
      if (!required) {
        delete payload[key];
      }
      return;
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      if (!required) {
        delete payload[key];
      }
      return;
    }
    payload[key] = parsed;
  }

  protected applySensitiveFields(payload: GungEdiInstanceConfiguration): void {
    this.sensitiveFields.forEach(field => {
      const newValue = this.form.get(this.getSensitiveControlName(field.key, 'NewValue'))?.value;
      const clear = !!this.form.get(this.getSensitiveControlName(field.key, 'Clear'))?.value;
      if (clear) {
        delete payload[field.key];
      } else if (newValue !== undefined && newValue !== null && newValue !== '') {
        payload[field.key] = newValue;
      }
    });
  }

  protected flattenFields(types: EdiConfigurationFieldType[]): EdiConfigurationField[] {
    return this.fieldGroups.flatMap(group => group.fields.filter(field => types.includes(field.type)));
  }

  protected joinArrayValue(value: unknown): string {
    if (!Array.isArray(value)) {
      return '';
    }
    return value.join('\n');
  }

  protected splitArrayValue(value: unknown): string[] {
    if (value === undefined || value === null || value === '') {
      return [];
    }
    return String(value)
      .split(/[\n,;]+/)
      .map(entry => entry.trim())
      .filter(entry => entry.length > 0);
  }

  protected cloneConfiguration(configuration: GungEdiInstanceConfiguration): GungEdiInstanceConfiguration {
    return JSON.parse(JSON.stringify(configuration));
  }

  protected sortConfigurations(configurations: GungEdiInstanceConfiguration[]): GungEdiInstanceConfiguration[] {
    return [...configurations].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  }

  protected loadSavedMappingModels(): void {
    this.mappingModelsLoading = true;
    this.mappingModelsError = '';
    this.ediService.getSavedMappingModels().subscribe({
      next: models => {
        this.savedOrderMappingModels = (models || [])
          .filter(model => model.targetMessageType === 'ORDER')
          .sort((a, b) => a.name.localeCompare(b.name));
        this.mappingModelsLoading = false;
        const selected = this.selectedConfiguration?.incomingOrderMappingModelId;
        if (selected) {
          this.loadMappingModelVersions(selected, this.selectedConfiguration?.incomingOrderMappingModelVersion);
        }
      },
      error: error => {
        this.mappingModelsError = this.getErrorMessage(error);
        this.mappingModelsLoading = false;
      }
    });
  }

  protected loadMappingModelVersions(modelId: string, selectedVersion?: number): void {
    this.mappingModelVersions = [];
    if (!modelId) {
      return;
    }
    this.mappingModelsLoading = true;
    this.mappingModelsError = '';
    this.ediService.getSavedMappingModelVersions(modelId).subscribe({
      next: versions => {
        this.mappingModelVersions = (versions || []).filter(model => model.targetMessageType === 'ORDER');
        this.mappingModelsLoading = false;
        const availableVersion = this.mappingModelVersions.find(model => model.version === selectedVersion)?.version;
        const nextVersion =
          selectedVersion === undefined ? (this.mappingModelVersions[0]?.version ?? null) : (availableVersion ?? null);
        this.form.get('incomingOrderMappingModelVersion')?.setValue(nextVersion);
        if (selectedVersion === nextVersion) {
          this.form.get('incomingOrderMappingModelVersion')?.markAsPristine();
        }
      },
      error: error => {
        this.mappingModelsError = this.getErrorMessage(error);
        this.mappingModelsLoading = false;
      }
    });
  }

  protected getErrorMessage(error: any): string {
    const fallback = this.translateService.instant('ERROR');
    const errorBody = error?.error;
    if (typeof errorBody === 'object' && errorBody !== null) {
      return errorBody.gungMessage || errorBody.message || errorBody.error || fallback;
    }
    if (typeof errorBody === 'string' && errorBody.trim()) {
      return errorBody.split('\n')[0].trim();
    }
    return error?.message || fallback;
  }
}
